/**
 * Catalogue local des courtiers SnapTrade.
 *
 * Les logos vivent dans `src/assets/brokers/`. La liste faisant autorité reste
 * celle renvoyée par l'API (`/api/snaptrade/brokerages`) : ce catalogue sert à
 * afficher quelque chose immédiatement et à substituer un logo local au logo S3
 * de SnapTrade. La réconciliation se fait sur un nom normalisé, parce que les
 * slugs SnapTrade ne sont pas ceux de leur site vitrine.
 */

const logoUrls = import.meta.glob('../assets/brokers/*.{webp,svg,png}', {
  eager: true,
  as: 'url',
}) as Record<string, string>;

const logoOf = (file: string): string | null => logoUrls[`../assets/brokers/${file}`] ?? null;

export type SnaptradeBrokerEntry = {
  /** Nom affiché. */
  name: string;
  /** Fichier du logo dans `src/assets/brokers/`. */
  file: string;
};

/** Les 40 courtiers pour lesquels un logo est fourni localement. */
const LOCAL_CATALOG: SnaptradeBrokerEntry[] = [
  { name: 'AJ Bell', file: 'aj-bell.webp' },
  { name: 'Alpaca', file: 'alpaca.webp' },
  { name: 'Binance', file: 'binance.webp' },
  { name: 'BMO InvestorLine', file: 'bmo.webp' },
  { name: 'BUX', file: 'bux.webp' },
  { name: 'Chase', file: 'chase.webp' },
  { name: "CIBC Investor's Edge", file: 'cibc.webp' },
  { name: 'Citi', file: 'citi.webp' },
  { name: 'Coinbase', file: 'coinbase.webp' },
  { name: 'Commsec', file: 'commsec.webp' },
  { name: 'DEGIRO', file: 'degiro.svg' },
  { name: 'E*TRADE', file: 'etrade.webp' },
  { name: 'Edward Jones', file: 'edward-jones.webp' },
  { name: 'Empower', file: 'empower.webp' },
  { name: 'eToro', file: 'etoro.webp' },
  { name: 'Fidelity', file: 'fidelity.webp' },
  { name: 'Interactive Brokers', file: 'ibkr.webp' },
  { name: 'Kraken', file: 'kraken.webp' },
  { name: 'Moomoo', file: 'moomoo.webp' },
  { name: 'PNC', file: 'pnc.webp' },
  { name: 'Public', file: 'public.webp' },
  { name: 'Questrade', file: 'questrade.webp' },
  { name: 'Robinhood', file: 'robinhood.webp' },
  { name: 'Schwab', file: 'schwab.webp' },
  { name: 'Stake AUS', file: 'stake-aus.webp' },
  { name: 'tastytrade', file: 'tastytrade.webp' },
  { name: 'TD Direct Investing', file: 'tddirectinvesting.webp' },
  { name: 'TIAA', file: 'tiaa.webp' },
  { name: 'TradeStation', file: 'tradestation.svg' },
  { name: 'Tradier', file: 'tradier.webp' },
  { name: 'Trading 212', file: 'trading212.webp' },
  { name: 'Transamerica', file: 'transamerica.webp' },
  { name: 'Upstox', file: 'upstox.webp' },
  { name: 'US Bank', file: 'us-bank.webp' },
  { name: 'Vanguard US', file: 'vanguard.webp' },
  { name: 'Wealthsimple', file: 'wealthsimple.webp' },
  { name: 'Webull Canada', file: 'webull-ca.webp' },
  { name: 'Webull US', file: 'webull.webp' },
  { name: 'Wells Fargo', file: 'wellsfargo.webp' },
  { name: 'Zerodha', file: 'zerodha.webp' },
];

/** "E*TRADE from Morgan Stanley" → "etradefrommorganstanley". */
export const normalizeBrokerKey = (value: string): string =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[^a-z0-9]/g, '');

/**
 * Clés supplémentaires par fichier de logo : SnapTrade nomme certains courtiers
 * autrement que leur marque commerciale.
 */
const EXTRA_KEYS: Record<string, string[]> = {
  'ibkr.webp': ['ibkr', 'interactivebrokers', 'interactivebrokerscom'],
  'etrade.webp': ['etrade', 'etradefrommorganstanley', 'morganstanleyetrade'],
  'tddirectinvesting.webp': ['td', 'tdameritrade', 'tddirectinvesting', 'tdcanadatrust'],
  'vanguard.webp': ['vanguard', 'vanguardus'],
  'webull.webp': ['webull', 'webullus'],
  'webull-ca.webp': ['webullca', 'webullcanada'],
  'stake-aus.webp': ['stake', 'stakeaus', 'stakeaustralia'],
  'bmo.webp': ['bmo', 'bmoinvestorline'],
  'cibc.webp': ['cibc', 'cibcinvestorsedge'],
  'us-bank.webp': ['usbank', 'usbancorp'],
  'public.webp': ['public', 'publiccom'],
  'schwab.webp': ['schwab', 'charlesschwab'],
  'alpaca.webp': ['alpaca', 'alpacapaper', 'alpacamarkets'],
  'trading212.webp': ['trading212'],
  'tastytrade.webp': ['tastytrade', 'tastyworks'],
  'aj-bell.webp': ['ajbell', 'ajbellyouinvest'],
  'edward-jones.webp': ['edwardjones'],
  'wellsfargo.webp': ['wellsfargo', 'wellsfargoadvisors'],
};

/** Index nom normalisé → URL du logo local. */
const LOGO_BY_KEY: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const entry of LOCAL_CATALOG) {
    const url = logoOf(entry.file);
    if (!url) continue;
    const keys = [normalizeBrokerKey(entry.name), ...(EXTRA_KEYS[entry.file] || [])];
    for (const key of keys) {
      if (key) map[key] = url;
    }
  }
  return map;
})();

/** Logo local d'un courtier, à partir de son nom et/ou de son slug SnapTrade. */
export const findLocalBrokerLogo = (...candidates: Array<string | null | undefined>): string | null => {
  for (const candidate of candidates) {
    if (!candidate) continue;
    const key = normalizeBrokerKey(candidate);
    if (LOGO_BY_KEY[key]) return LOGO_BY_KEY[key];
    // "ALPACA-PAPER" → "alpaca"
    const trimmed = key.replace(/(paper|api|us|ca|uk|au)$/, '');
    if (trimmed && LOGO_BY_KEY[trimmed]) return LOGO_BY_KEY[trimmed];
  }
  return null;
};

export type SnaptradeBroker = {
  id: string | null;
  /** Slug SnapTrade — requis pour présélectionner le courtier dans le portail. */
  slug: string | null;
  name: string;
  logo: string | null;
  maintenanceMode: boolean;
  isDegraded: boolean;
};

/** Catalogue affiché tant que l'API n'a pas répondu (aucun slug connu). */
export const FALLBACK_BROKERS: SnaptradeBroker[] = LOCAL_CATALOG.map((entry) => ({
  id: null,
  slug: null,
  name: entry.name,
  logo: logoOf(entry.file),
  maintenanceMode: false,
  isDegraded: false,
}));
